import { IconX } from '@tabler/icons-react';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import CartItemCustomizations from '~/components/cart/CartItemCustomizations';
import ImagePlaceholder from '~/components/ImagePlaceholder';
import TouchSpin from '~/components/TouchSpin';
import { useDropcart } from '~/context/DropcartContext';
import { useOrderItemDataClient } from '~/hooks/useOrderItemDataClient';
import type { OrderItem } from '~/types/Order';
import { useFormatPrice } from '~/utils/price';
import { getMaxOrderQuantity } from '~/utils/stock';

interface DropcartItemProps {
  item: OrderItem;
  currencyCode: string;
  onQuantityChange: (_id: number, _quantity: number) => void;
  onRemove: (_id: number) => void;
}

const DEBOUNCE_DELAY = 500;

const DropcartItem: React.FC<DropcartItemProps> = ({ item, currencyCode, onQuantityChange, onRemove }) => {
  const [localQuantity, setLocalQuantity] = useState(item.quantity ?? 1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formatPrice = useFormatPrice();
  const { t } = useTranslation();
  const { closeDropcart } = useDropcart();

  const { variant, optionValues, productUrl, image } = useOrderItemDataClient(item);

  const maxQty = getMaxOrderQuantity(variant);

  useEffect(() => {
    setLocalQuantity(item.quantity ?? 1);
  }, [item.quantity]);

  const handleQuantityChange = useCallback(
    (qty: number) => {
      setLocalQuantity(qty);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        if (item.id) {
          onQuantityChange(item.id, qty);
        }
      }, DEBOUNCE_DELAY);
    },
    [item.id, onQuantityChange]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="dropcart-item">
      <div className="dropcart-item__image">
        {image ? (
          <Image
            src={image}
            alt={item.productName ?? ''}
            loading="lazy"
            fetchpriority="low"
            decoding="async"
            width={64}
            height={64}
          />
        ) : (
          <ImagePlaceholder />
        )}
      </div>
      <div className="dropcart-item__content">
        <div className="dropcart-item__product">
          <Link to={productUrl} className="dropcart-item__name" onClick={closeDropcart}>
            <span className="fw-semibold">{item.productName ?? ''}</span>
          </Link>
          {optionValues.map((ov, i) => (
            <div key={i} className="dropcart-item__option">
              {ov.option?.name ?? ov.option?.code} {ov.value}
            </div>
          ))}
          <CartItemCustomizations customizations={item.customizations ?? []} variant="compact" placement="bottom" />
        </div>
        <div className="dropcart-item__actions">
          <TouchSpin
            value={localQuantity}
            onChange={handleQuantityChange}
            min={1}
            max={maxQty ?? 99999}
            className="touchspin--xs"
          />
        </div>
        <div className="dropcart-item__price-wrapper text-end">
          <Button
            variant="link"
            className="dropcart-item__remove ms-auto p-0 ps-2"
            onClick={() => item.id && onRemove(item.id)}
            aria-label={t('components.dropcart.remove_item')}
          >
            <IconX size={18} stroke={3} />
          </Button>
          <div className="dropcart-item__price fw-bold">{formatPrice(item.total, currencyCode)}</div>
        </div>
      </div>
    </div>
  );
};

export default DropcartItem;
